import {
  RestaurantTag,
  RestaurantTagsResponse,
} from '@/lib/types/customer/RestaurantTags';
import { create } from 'zustand';

type RestaurantState = {
  tagsData: RestaurantTagsResponse | null;
  selectedTag: RestaurantTag | null;
  showChildrenTag: boolean;
  isTagsLoading: boolean;
  childrenTags: RestaurantTag[] | [];
  showViewAllTag: boolean;
  isChildTag: boolean;
  selectedSearchableTags: RestaurantTag[];
  selectedParentTag: RestaurantTag | null;
  selectedChildTags: RestaurantTag[];
  swiperRefs: Map<string, any>;
  showSwiperNav: Record<string, Record<string, boolean>>;
  scrollToSlideFirstTime: boolean;
};

type RestaurantActions = {
  setRestaurantTagsData: (
    tagsData: RestaurantTagsResponse,
    vendorType?: string
  ) => void;
  setSelectedTag: (tag: RestaurantTag | null) => void;
  setShowChildrenTag: (val: boolean) => void;
  setIsTagsLoading: (val: boolean) => void;
  setShowViewAllTag: (val: boolean) => void;
  setIsChildTag: (val: boolean) => void;
  setChildrenTags: (tags: RestaurantTag[] | []) => void;
  setSelectedSearchableTags: (tag?: RestaurantTag, isParent?: boolean) => void;
  setSelectedBulkSearchableTags: (tag: RestaurantTag[]) => void;
  setSelectedParentTag: (tag?: RestaurantTag) => void;
  setSelectedChildTags: (tag?: RestaurantTag) => void;
  resetSearchableTags: () => void;
  setSwiperRefs: (swiper: any, id: string) => void;
  setShowSwiperNav: (id: string) => void;
  setScrollToSlideFirstTime: (value: boolean) => void;
  resetStoreState: () => void;
};

const initialStoreState: RestaurantState = {
  isTagsLoading: false,
  tagsData: null,
  selectedTag: null,
  showChildrenTag: false,
  showViewAllTag: false,
  isChildTag: false,
  childrenTags: [],
  selectedParentTag: null,
  selectedChildTags: [],
  scrollToSlideFirstTime: false,
  swiperRefs: new Map<string, any>(),
  showSwiperNav: {
    parent_tag: { isBeginning: true, isEnd: false },
    child_tag: { isBeginning: true, isEnd: false },
  },
  selectedSearchableTags: [],
};

export const useRestaurantTagsDataStore = create<
  RestaurantState & RestaurantActions
>((set, get) => ({
  ...initialStoreState,
  resetStoreState: () => {
    set(() => ({ ...initialStoreState }));
  },
  setScrollToSlideFirstTime: (value) => {
    set({ scrollToSlideFirstTime: value });
  },
  setSwiperRefs: (swiper, id) => {
    if (swiper && id) {
      const allSwiperRefs = get().swiperRefs;
      allSwiperRefs.set(id, swiper);
      set({ swiperRefs: allSwiperRefs });
    } else {
      // console.error(`Failed to initialize Swiper with ID: ${id}`);
    }
  },
  setShowSwiperNav: (id) => {
    const copiedState = get().showSwiperNav;
    const swiper = get().swiperRefs.get(id);
    set({
      showSwiperNav: {
        ...copiedState,
        [id]: { isBeginning: swiper.isBeginning, isEnd: swiper.isEnd },
      },
    });
  },
  setSelectedParentTag: (tag) => {
    if (tag) {
      set({ selectedParentTag: tag });
    } else {
      set({ selectedParentTag: null });
    }
  },
  setSelectedChildTags: (tag) => {
    if (!tag) {
      set({ selectedSearchableTags: [] });
      return;
    }
    const copiedChildTags = get().selectedChildTags;
    const isExists = copiedChildTags?.find((item) => item.id === tag.id);
    const onlyChildren = copiedChildTags.filter((item) => item?.parent_id);
    if (isExists) {
      const filteredSearchableTags = onlyChildren?.filter(
        (item) => item.id !== tag.id
      );
      set({
        selectedChildTags: filteredSearchableTags?.toSorted(
          (a, b) => Number(a.id) - Number(b.id)
        ),
      });
    } else {
      set({
        selectedChildTags:
          onlyChildren?.length > 0
            ? [...onlyChildren, tag]?.sort(
                (a, b) => Number(a.id) - Number(b.id)
              )
            : [tag],
      });
    }
  },
  resetSearchableTags: () => {
    set({ selectedSearchableTags: [] });
  },
  setSelectedBulkSearchableTags: (tags) => {
    set({ selectedSearchableTags: tags });
  },
  setSelectedSearchableTags: (tag) => {
    if (!tag) {
      set({ selectedSearchableTags: [] });
      return;
    }
    const copiedSearchableTags = get().selectedSearchableTags;
    const isExists = copiedSearchableTags?.find((item) => item.id === tag.id);
    const onlyChildren = copiedSearchableTags.filter((item) => item?.parent_id);
    if (isExists) {
      const filteredSearchableTags = onlyChildren?.filter(
        (item) => item.id !== tag.id
      );
      set({ selectedSearchableTags: filteredSearchableTags });
    } else {
      set({
        selectedSearchableTags:
          onlyChildren?.length > 0 ? [...onlyChildren, tag] : [tag],
      });
    }
  },
  setChildrenTags: (tags: RestaurantTag[] | []) => {
    const updatedTags = tags.sort(
      (a, b) => Number(a.order_index) - Number(b.order_index)
    );

    set({
      childrenTags: updatedTags,
    });
  },
  setRestaurantTagsData: (
    tagsData: RestaurantTagsResponse,
    vendorType?: string
  ) => {
    const updatedTagsData = {
      ...tagsData,
      // data: tagsData.data.sort(
      //   (a, b) => Number(a.order_index) - Number(b.order_index)
      // ),
    };

    set({ tagsData: updatedTagsData });

    const url = new URL(window.location.href);
    const paramsTagIds = Object.fromEntries(url.searchParams.entries())
      .tag_ids?.split(',')
      .map((item) => parseInt(item));

    if (paramsTagIds && paramsTagIds?.length > 0) {
      const flatTags = flattenTags(updatedTagsData?.data ?? []);
      const selectedFilteredTags: RestaurantTag[] | undefined =
        flatTags?.filter((tag: any) => paramsTagIds?.includes(tag.id));

      if (selectedFilteredTags && selectedFilteredTags?.length > 0) {
        set({ selectedSearchableTags: selectedFilteredTags });
        let parentTagSlidedTo = false;
        let childTagSlidedTo = false;
        selectedFilteredTags?.forEach((tag) => {
          // If parent tag exists on search param then set parent tag and children tags
          if (!tag?.parent_id && !parentTagSlidedTo) {
            const parentTagIdx =
              updatedTagsData?.data?.findIndex((item) => item.id === tag?.id) ??
              -1;
            parentTagSlidedTo = true;

            if (parentTagIdx > -1) {
              set({
                childrenTags: (
                  updatedTagsData?.data?.[parentTagIdx]?.children ?? []
                )?.sort(
                  (a, b) => Number(a.order_index) - Number(b.order_index)
                ),
              });
              set({ showChildrenTag: true });
              set({ selectedParentTag: tag });
            }
          }

          // If children tag exist on search params then set parent tag and children tags
          if (!childTagSlidedTo && tag?.parent_id) {
            const findParentTag = updatedTagsData?.data.find(
              (item) => item.id === tag?.parent_id
            );
            if (findParentTag) {
              set({ selectedParentTag: findParentTag });

              childTagSlidedTo = true;
              set({
                childrenTags: findParentTag.children?.toSorted(
                  (a, b) => Number(a.order_index) - Number(b.order_index)
                ),
              });
              set({ showChildrenTag: true });
              set({
                selectedChildTags: [...get().selectedChildTags, tag]?.sort(
                  (a, b) => Number(a.id) - Number(b.id)
                ),
              });
            }
          }
        });
        set({ scrollToSlideFirstTime: true });
      }
    }
  },
  setSelectedTag: (tag) => {
    set({ selectedTag: tag });
  },
  setShowChildrenTag: (val) => {
    set({ showChildrenTag: val });
  },
  setShowViewAllTag: (val) => {
    set({ showViewAllTag: val });
  },
  setIsChildTag: (val) => {
    set({ isChildTag: val });
  },
  setIsTagsLoading: (val) => {
    set({ isTagsLoading: val });
  },
}));

function flattenTags(tags: RestaurantTag[]): RestaurantTag[] {
  const result: RestaurantTag[] = [];

  function flatten(tagList: RestaurantTag[]) {
    tagList.forEach((tag) => {
      // Add the current tag to the result
      result.push({ ...tag, children: [] }); // Exclude children in the flat structure
      // Recursively flatten the children
      if (tag.children && tag.children.length > 0) {
        flatten(tag.children);
      }
    });
  }

  flatten(tags);
  return result;
}
